/**
 * TypeScript type definitions for Project Estimation System
 * Phase 3: SpecKit Integration & Queue System
 */

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export enum EstimationStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  CLARIFICATION_NEEDED = 'clarification_needed',
  GENERATING_SPECIFICATION = 'generating_specification',
  SPECIFICATION_COMPLETE = 'specification_complete',
  REVIEWED = 'reviewed',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  CONVERTED_TO_PROJECT = 'converted_to_project',
}

export enum ClarificationQuestionType {
  TECHNICAL = 'technical',
  BUSINESS = 'business',
  SCOPE = 'scope',
  TIMELINE = 'timeline',
  BUDGET = 'budget',
  RESOURCES = 'resources',
  INTEGRATION = 'integration',
  COMPLIANCE = 'compliance',
}

export enum QueueStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export enum NoteType {
  GENERAL = 'general',
  TECHNICAL = 'technical',
  BUSINESS = 'business',
  RISK = 'risk',
  RECOMMENDATION = 'recommendation',
}

export enum AIProvider {
  CLAUDE = 'claude',
  OPENAI = 'openai',
  GEMINI = 'gemini',
  MOCK = 'mock',
}

// ============================================================================
// MAIN INTERFACES
// ============================================================================

export interface ProjectEstimation {
  id: number;
  client_id: number;
  submitted_by_user_id: number;
  project_name: string;
  project_description: string;
  project_types: string[];
  estimated_budget_range?: string;
  estimated_timeline?: string;
  technical_requirements?: Record<string, any>;
  business_requirements?: Record<string, any>;
  tech_stack_preferences?: string[];
  status: EstimationStatus;
  submitted_at?: string;
  specification_generated_at?: string;
  approved_at?: string;
  approved_by_user_id?: number;
  rejected_at?: string;
  rejection_reason?: string;
  converted_project_id?: number;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
  deleted_at?: string;

  // Relationships
  client?: {
    id: number;
    name: string;
    email: string;
    company_name?: string;
  };
  submitted_by?: {
    id: number;
    name: string;
    email: string;
  };
  approved_by?: {
    id: number;
    name: string;
    email: string;
  };
  clarifications?: EstimationClarification[];
  queue_jobs?: SpecKitGenerationQueue[];
  specifications?: GeneratedSpecification[];
  latest_specification?: GeneratedSpecification;
  notes?: EstimationNote[];
  converted_project?: {
    id: number;
    name: string;
    status: string;
  };

  // Computed fields
  status_label: string;
  status_color: string;
  clarification_progress: number;
  has_all_required_clarifications_answered: boolean;
  can_generate_specification: boolean;
  can_be_approved: boolean;
  is_completed: boolean;
  days_since_submission: number;
  estimated_hours?: number;
  estimated_cost_usd?: number;
}

export interface EstimationClarification {
  id: number;
  project_estimation_id: number;
  question: string;
  question_type: ClarificationQuestionType;
  is_required: boolean;
  answer?: string;
  answered_at?: string;
  clarification_round: number;
  sort_order: number;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;

  // Relationships
  estimation?: ProjectEstimation;

  // Computed
  is_answered: boolean;
  question_type_label: string;
}

export interface SpecKitGenerationQueue {
  id: number;
  project_estimation_id: number;
  ai_provider: AIProvider;
  status: QueueStatus;
  priority: number;
  input_data?: Record<string, any>;
  ai_model?: string;
  result?: Record<string, any>;
  error_message?: string;
  retry_count: number;
  max_retries: number;
  processing_started_at?: string;
  processing_completed_at?: string;
  next_retry_at?: string;
  created_at: string;
  updated_at: string;

  // Relationships
  estimation?: ProjectEstimation;
  generated_specification?: GeneratedSpecification;

  // Computed
  status_label: string;
  status_color: string;
  can_retry: boolean;
  processing_duration_seconds?: number;
  waiting_duration_seconds?: number;
}

export interface GeneratedSpecification {
  id: number;
  project_estimation_id: number;
  queue_job_id?: number;
  version: number;
  is_latest: boolean;
  ai_provider: AIProvider;
  ai_model?: string;
  product_definition?: string;
  data_model?: string;
  task_breakdown?: string;
  api_specification?: string;
  ui_wireframes?: string;
  full_specification?: string;
  estimated_hours?: number;
  estimated_cost_usd?: number;
  estimated_timeline?: string;
  tech_stack_used?: string[];
  generation_metadata?: Record<string, any>;
  token_usage?: Record<string, any>;
  generation_cost_usd?: number;
  generation_time_seconds?: number;
  created_at: string;
  updated_at: string;

  // Relationships
  estimation?: ProjectEstimation;
  queue_job?: SpecKitGenerationQueue;

  // Computed
  is_complete: boolean;
  completion_percentage: number;
  formatted_estimated_cost: string;
  formatted_generation_time: string;
}

export interface EstimationNote {
  id: number;
  project_estimation_id: number;
  user_id: number;
  note_type: NoteType;
  content: string;
  is_internal: boolean;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;

  // Relationships
  estimation?: ProjectEstimation;
  user?: {
    id: number;
    name: string;
    email: string;
    role: string;
  };

  // Computed
  note_type_label: string;
  visibility_label: string;
}

// ============================================================================
// REQUEST/RESPONSE INTERFACES
// ============================================================================

export interface SubmitEstimationRequest {
  project_name: string;
  project_description: string;
  project_types: string[];
  estimated_budget_range?: string;
  estimated_timeline?: string;
  technical_requirements?: Record<string, any>;
  business_requirements?: Record<string, any>;
  tech_stack_preferences?: string[];
}

export interface AnswerClarificationsRequest {
  answers: {
    clarification_id: number;
    answer: string;
  }[];
}

export interface ReviewEstimationRequest {
  notes: string;
  is_internal?: boolean;
}

export interface ApproveEstimationRequest {
  notes?: string;
}

export interface RejectEstimationRequest {
  rejection_reason: string;
  notes?: string;
}

export interface ConvertToProjectRequest {
  notes?: string;
}

export interface RegenerateSpecificationRequest {
  ai_provider?: AIProvider;
  priority?: number;
}

export interface AddNoteRequest {
  note_type: NoteType;
  content: string;
  is_internal?: boolean;
}

// ============================================================================
// FILTER & PAGINATION INTERFACES
// ============================================================================

export interface EstimationFilters {
  status?: EstimationStatus | EstimationStatus[];
  client_id?: number;
  submitted_by_user_id?: number;
  date_from?: string;
  date_to?: string;
  search?: string;
  project_type?: string;
  has_specification?: boolean;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

export interface QueueStatusResponse {
  pending_count: number;
  processing_count: number;
  completed_today_count: number;
  failed_today_count: number;
  average_processing_time_seconds: number;
  oldest_pending_job?: {
    id: number;
    project_estimation_id: number;
    project_name: string;
    created_at: string;
    waiting_hours: number;
  };
}

// ============================================================================
// UI STATE INTERFACES
// ============================================================================

export interface EstimationFormState {
  project_name: string;
  project_description: string;
  project_types: string[];
  estimated_budget_range: string;
  estimated_timeline: string;
  technical_requirements: string;
  business_requirements: string;
  tech_stack_preferences: string[];
}

export interface ClarificationAnswerState {
  [clarificationId: number]: string;
}

export interface EstimationReviewState {
  notes: string;
  is_internal: boolean;
  action: 'approve' | 'reject' | 'review' | null;
  rejection_reason: string;
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

export type EstimationStatusColors = {
  [key in EstimationStatus]: string;
};

export type QueueStatusColors = {
  [key in QueueStatus]: string;
};

export const ESTIMATION_STATUS_LABELS: Record<EstimationStatus, string> = {
  [EstimationStatus.DRAFT]: 'Draft',
  [EstimationStatus.SUBMITTED]: 'Submitted',
  [EstimationStatus.CLARIFICATION_NEEDED]: 'Clarification Needed',
  [EstimationStatus.GENERATING_SPECIFICATION]: 'Generating Specification',
  [EstimationStatus.SPECIFICATION_COMPLETE]: 'Specification Complete',
  [EstimationStatus.REVIEWED]: 'Reviewed',
  [EstimationStatus.APPROVED]: 'Approved',
  [EstimationStatus.REJECTED]: 'Rejected',
  [EstimationStatus.CONVERTED_TO_PROJECT]: 'Converted to Project',
};

export const ESTIMATION_STATUS_COLORS: EstimationStatusColors = {
  [EstimationStatus.DRAFT]: 'gray',
  [EstimationStatus.SUBMITTED]: 'blue',
  [EstimationStatus.CLARIFICATION_NEEDED]: 'orange',
  [EstimationStatus.GENERATING_SPECIFICATION]: 'purple',
  [EstimationStatus.SPECIFICATION_COMPLETE]: 'cyan',
  [EstimationStatus.REVIEWED]: 'teal',
  [EstimationStatus.APPROVED]: 'green',
  [EstimationStatus.REJECTED]: 'red',
  [EstimationStatus.CONVERTED_TO_PROJECT]: 'emerald',
};

export const QUEUE_STATUS_LABELS: Record<QueueStatus, string> = {
  [QueueStatus.PENDING]: 'Pending',
  [QueueStatus.PROCESSING]: 'Processing',
  [QueueStatus.COMPLETED]: 'Completed',
  [QueueStatus.FAILED]: 'Failed',
  [QueueStatus.CANCELLED]: 'Cancelled',
};

export const QUEUE_STATUS_COLORS: QueueStatusColors = {
  [QueueStatus.PENDING]: 'yellow',
  [QueueStatus.PROCESSING]: 'blue',
  [QueueStatus.COMPLETED]: 'green',
  [QueueStatus.FAILED]: 'red',
  [QueueStatus.CANCELLED]: 'gray',
};

export const CLARIFICATION_TYPE_LABELS: Record<ClarificationQuestionType, string> = {
  [ClarificationQuestionType.TECHNICAL]: 'Technical',
  [ClarificationQuestionType.BUSINESS]: 'Business',
  [ClarificationQuestionType.SCOPE]: 'Scope',
  [ClarificationQuestionType.TIMELINE]: 'Timeline',
  [ClarificationQuestionType.BUDGET]: 'Budget',
  [ClarificationQuestionType.RESOURCES]: 'Resources',
  [ClarificationQuestionType.INTEGRATION]: 'Integration',
  [ClarificationQuestionType.COMPLIANCE]: 'Compliance',
};

export const NOTE_TYPE_LABELS: Record<NoteType, string> = {
  [NoteType.GENERAL]: 'General',
  [NoteType.TECHNICAL]: 'Technical',
  [NoteType.BUSINESS]: 'Business',
  [NoteType.RISK]: 'Risk',
  [NoteType.RECOMMENDATION]: 'Recommendation',
};
